import {
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import {
  DraftAssociation,
  DraftGroup,
  DraftNode,
  DraftOpaque,
  draftAssociation,
  draftCondition,
  draftGroup
} from '@core/filters/path-filter/draft';
import { printFilter } from '@core/filters/path-filter/dsl/printer';
import { Quantifier, edgePath } from '@core/filters/path-filter/wire';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { ICONS } from '@core/services/icons/icons.service';

import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { FocusFirstDirective } from '@shared/directives/focus-first.directive';
import { AvailableLanguages } from '@shared/types/translation.type';

import { PathFilterAssociationComponent } from '../association/path-filter-association.component';
import { PATH_FILTER_COMPOSER_STATE } from '../composer/path-filter-composer-state';
import { PathFilterConditionComponent } from '../condition/path-filter-condition.component';
import { DslTextComponent } from '../dsl-text/dsl-text.component';
import { GROUP_MODES, GroupMode, groupModeOf } from '../group-mode';
import { associationLabel, scopedAssociationLabel } from '../path-filter-labels';
import { ComposerContext } from '../path-filter.models';

@Component({
  selector: 'app-path-filter-group',
  imports: [
    TranslatePipe,
    DropdownComponent,
    DslTextComponent,
    FocusFirstDirective,
    PathFilterConditionComponent,
    PathFilterAssociationComponent
  ],
  templateUrl: './path-filter-group.component.html',
  styles: `
    :host {
      display: block;
    }
  `
})
export class PathFilterGroupComponent {
  group = input.required<DraftGroup>();
  edge = input<string | null>(null);
  context = input.required<ComposerContext>();
  /** The root cannot be removed; an association's group goes with its block. */
  placement = input<'root' | 'association' | 'nested'>('nested');

  /** Names the level the Add button adds to; the root's and a nested group's default to their own wording. */
  addLabel = input<string | null>(null);

  groupChange = output<DraftGroup>();
  removed = output<void>();

  protected readonly ICONS = ICONS;
  private readonly translate = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;

  private readonly composerState = inject(PATH_FILTER_COMPOSER_STATE);
  protected readonly hovered = this.composerState.hovered;

  protected readonly addMenuOpen = signal(false);
  protected readonly closeAddMenu = () => this.addMenuOpen.set(false);
  private readonly addButton = viewChild.required<ElementRef<HTMLElement>>('addButton');

  protected readonly modes = Object.keys(GROUP_MODES) as GroupMode[];
  protected readonly mode = computed(() => groupModeOf(this.group()));

  /** The vocabulary says which associations a scope may hold: the root's, or one level down. */
  protected readonly associations = computed(() => {
    const lang = this.currentLang() as AvailableLanguages;
    return this.context()
      .vocabulary.associations(this.edge())
      .map((association) => ({
        edge: association.edge,
        label: associationLabel(association, lang, this.translate),
        cardinality: association.cardinality,
        quantifier: (association.quantifiers[0] ?? 'any') as Quantifier
      }));
  });

  protected readonly addText = computed(() => {
    this.currentLang();
    return (
      this.addLabel() ??
      this.translate.instant(
        `features.data.path_filter.add.${this.placement() === 'nested' ? 'to_group' : 'label'}`
      )
    );
  });

  /** "Add to actions" per block, by block id: the served title, lower-cased mid-phrase. */
  protected readonly addLabels = computed(() => {
    const lang = this.currentLang() as AvailableLanguages;
    const vocabulary = this.context().vocabulary;
    const labels = new Map<string, string>();
    for (const child of this.group().children) {
      if (child.kind !== 'association') continue;
      const target = scopedAssociationLabel(
        vocabulary,
        this.edge(),
        child.edge,
        lang,
        this.translate
      ).toLocaleLowerCase(lang);
      labels.set(
        child.id,
        this.translate.instant('features.data.path_filter.add.to', { target })
      );
    }
    return labels;
  });

  constructor() {
    effect(() => {
      if (this.composerState.openAddMenuFor() !== this.group().id) return;
      this.composerState.openAddMenuFor.set(null);
      this.addMenuOpen.set(true);
    });
  }

  // The overlay stops key events at its panels, so its own Escape listener never sees these.
  protected onAddMenuKey(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    this.closeAddMenu();
    this.addButton().nativeElement.focus();
  }

  protected setMode(mode: GroupMode): void {
    if (mode !== this.mode()) {
      this.groupChange.emit({ ...this.group(), ...GROUP_MODES[mode] });
    }
  }

  protected replaceChild(index: number, child: DraftNode): void {
    const children = [...this.group().children];
    children[index] = child;
    this.groupChange.emit({ ...this.group(), children });
  }

  protected replaceWithAssociation(
    index: number,
    edge: string,
    quantifier: Quantifier
  ): void {
    this.replaceChild(index, this.emptyBlock(edge, quantifier));
  }

  protected replaceAssociationFilter(
    index: number,
    association: DraftAssociation,
    filter: DraftGroup
  ): void {
    this.replaceChild(index, { ...association, filter });
  }

  protected removeChild(index: number): void {
    this.groupChange.emit({
      ...this.group(),
      children: this.group().children.filter((_, position) => position !== index)
    });
  }

  protected addCondition(): void {
    this.add(draftCondition());
  }

  protected addGroup(): void {
    const group = draftGroup(this.group().connector === 'and' ? 'or' : 'and');
    this.composerState.openAddMenuFor.set(group.id);
    this.add(group);
  }

  protected addAssociation(edge: string, quantifier: Quantifier): void {
    this.add(this.emptyBlock(edge, quantifier));
  }

  /** The user picks what goes inside, so the block starts empty with its own add menu open. */
  private emptyBlock(edge: string, quantifier: Quantifier): DraftAssociation {
    const block = { ...draftAssociation(edge, quantifier), filter: draftGroup('and') };
    this.composerState.openAddMenuFor.set(block.filter.id);
    return block;
  }

  private add(child: DraftNode): void {
    this.addMenuOpen.set(false);
    this.groupChange.emit({ ...this.group(), children: [...this.group().children, child] });
  }

  protected scopeOf(association: DraftAssociation): string {
    return edgePath(this.edge(), association.edge);
  }

  protected opaqueText(node: DraftOpaque): string {
    return printFilter(node.negated ? { not: node.expr } : node.expr);
  }
}
